import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../utils/apiClient';

const PAGE_SIZE = 20;

/**
 * @param {string|null} q - поисковый запрос
 * @param {Object} [options]
 * @param {number} [options.limit=PAGE_SIZE] - размер страницы
 * @param {boolean} [options.enabled=true] - если false, запрос не выполняется
 *   (например, пока пользователь ничего не ввёл в поиске)
 */
export function useUsers(q = null, { limit = PAGE_SIZE, enabled = true } = {}) {
    const query = (q ?? '').trim();

    const [users, setUsers] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const pageRef = useRef(1);
    const loadingMoreRef = useRef(false);

    const buildUrl = useCallback((page) => {
        const url = new URL('/api/users', process.env.REACT_APP_API_URL);
        url.searchParams.append('page', page);
        url.searchParams.append('limit', limit);
        if (query) url.searchParams.append('q', query);
        return url.toString();
    }, [query, limit]);

    const authHeaders = () => {
        const accessToken = localStorage.getItem('accessToken') ?? null;
        return {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        };
    };

    // Первичная загрузка + сброс при смене запроса
    useEffect(() => {
        let isMounted = true;

        // Запрос отключён — ничего не грузим и очищаем прошлый результат
        if (!enabled) {
            setUsers(null);
            setError(null);
            setHasMore(false);
            setLoading(false);
            pageRef.current = 1;
            return;
        }

        const fetchInitial = async () => {
            try {
                setLoading(true);
                setError(null);
                pageRef.current = 1;

                const response = await apiFetch(buildUrl(1), {
                    method: 'GET',
                    credentials: 'include',
                    headers: authHeaders(),
                });

                const data = await response.json();
                if (!response.ok) throw new Error('Ошибка сервера');

                if (isMounted) {
                    if (data.error) throw new Error(data.error);
                    setUsers(data.users ?? []);
                    setHasMore(!!data.hasMore);
                }
            } catch (err) {
                if (isMounted) setError(err.message);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchInitial();

        return () => {
            isMounted = false;
        };
    }, [buildUrl, enabled]);

    const loadMore = useCallback(async () => {
        if (loadingMoreRef.current || !enabled) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);

        try {
            const nextPage = pageRef.current + 1;
            const response = await apiFetch(buildUrl(nextPage), {
                method: 'GET',
                credentials: 'include',
                headers: authHeaders(),
            });

            const data = await response.json();
            if (!response.ok || data.error) throw new Error(data.error ?? 'Ошибка сервера');

            setUsers(prev => [...(prev ?? []), ...(data.users ?? [])]);
            pageRef.current = nextPage;
            setHasMore(!!data.hasMore);
        } catch (err) {
            setHasMore(false);
        } finally {
            loadingMoreRef.current = false;
            setLoadingMore(false);
        }
    }, [buildUrl, enabled]);

    return { users, loading, error, hasMore, loadingMore, loadMore };
}
